import { useCallback, useEffect, useState } from 'react';
import { listAllEmployees, peopleGateway } from '../../people';
import type { EmployeeResponse } from '../../people/types';
import { reportsGateway } from '../index';
import type { EmployeePaySummaryResponse } from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

const DEFAULT_FROM = '2026-01-01';
const DEFAULT_TO = '2026-12-31';

export function EmployeePaySummaryPage() {
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [from, setFrom] = useState(DEFAULT_FROM);
  const [to, setTo] = useState(DEFAULT_TO);
  const [summary, setSummary] = useState<EmployeePaySummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // This picker needs the complete employee collection, not one page of it — follows every page of
      // the server-paginated Employee List query via the shared load-all helper.
      const all = await listAllEmployees(peopleGateway, { sortBy: 'name' });
      setEmployees(all);
      setEmployeeId((current) => current || all[0]?.id || '');
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load employees.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  async function handleView() {
    if (!employeeId) return;
    setError(null);
    try {
      setSummary(await reportsGateway.getEmployeePaySummary(employeeId, from, to));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load the pay summary.'));
    }
  }

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Employee Pay Summary</h2>
      </div>

      {error && <p className="error">{error}</p>}
      {loading && <p className="muted">Loading employees…</p>}

      {!loading && (
        <form className="inline-form" onSubmit={(e) => { e.preventDefault(); void handleView(); }}>
          <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>{e.employeeNumber} — {e.firstName} {e.lastName}</option>
            ))}
          </select>
          <label className="inline-form-date-label">
            From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="inline-form-date-label">
            To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
          <button type="submit">View</button>
        </form>
      )}

      {summary && (
        <>
          <p>{summary.employeeNumber} — {summary.employeeName} ({summary.from} to {summary.to})</p>
          <table>
            <thead>
              <tr><th>Check Date</th><th>Gross</th><th>Employee Taxes</th><th>Employer Taxes</th><th>Net Pay</th></tr>
            </thead>
            <tbody>
              {summary.lines.length === 0 && (
                <tr><td colSpan={5} className="muted">No Approved payroll runs in this range.</td></tr>
              )}
              {summary.lines.map((line) => (
                <tr key={line.payrollRunId}>
                  <td>{line.checkDate}</td>
                  <td>{line.grossEarnings.toFixed(2)}</td>
                  <td>{line.employeeTaxes.toFixed(2)}</td>
                  <td>{line.employerTaxes.toFixed(2)}</td>
                  <td>{line.netPay.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Totals</strong></td>
                <td>{summary.totals.grossEarnings.toFixed(2)}</td>
                <td>{summary.totals.employeeTaxes.toFixed(2)}</td>
                <td>{summary.totals.employerTaxes.toFixed(2)}</td>
                <td>{summary.totals.netPay.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </>
      )}
    </section>
  );
}
